import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ArrowRight, Check, Users, Truck, Mail } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const GARMENTS = ["T-Shirt", "Polo", "Hoodie", "Sweatshirt", "Long Sleeve"];
const OCCASIONS = ["Church", "Birthday", "Family reunion", "Business", "Team", "Event", "Wedding", "School", "Group"];

export default function GroupOrders() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", organization: "",
    occasion: "Church", garment: "T-Shirt", quantity: "30", colors: "",
    sizes: "", dateNeeded: "", designNotes: "", hasDesign: "yes",
  });
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const update = (k, v) => setForm({ ...form, [k]: v });
  const submit = (e) => {
    e.preventDefault();
    const existing = JSON.parse(localStorage.getItem("dms_group_quotes") || "[]");
    localStorage.setItem("dms_group_quotes", JSON.stringify([...existing, { ...form, submittedAt: new Date().toISOString() }]));
    toast({ title: "Quote request sent!", description: "Hristina will email you a custom price within 1 business day." });
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-[color:var(--dms-cream)]">
      <Navbar />
      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-16">
        <p className="kicker text-center">Group Orders</p>
        <h1 className="font-serif text-4xl md:text-5xl text-center text-[color:var(--dms-navy)] mt-4">Request a custom group quote</h1>
        <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
        <p className="mt-6 text-center text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">Tell us what you're ordering. We'll review the details and email you an exact price — no charge until you approve the proof.</p>

        <div className="mt-8 flex items-center justify-center gap-6 flex-wrap text-sm text-[color:var(--dms-navy)]/70">
          <span className="flex items-center gap-2"><Users className="w-4 h-4 text-[color:var(--dms-peach)]" /> No minimum</span>
          <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-[color:var(--dms-peach)]" /> Free shipping over $50</span>
          <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[color:var(--dms-peach)]" /> Proof before print</span>
        </div>

        {submitted ? (
          <div className="mt-12 bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-[color:var(--dms-peach)] mx-auto flex items-center justify-center">
              <Mail className="w-7 h-7 text-[color:var(--dms-navy)]" />
            </div>
            <h2 className="font-serif text-3xl text-[color:var(--dms-navy)] mt-6">Thank you, {form.name.split(" ")[0] || "friend"}!</h2>
            <p className="mt-3 text-[color:var(--dms-navy)]/70 max-w-lg mx-auto">Your quote request was received. We'll email <span className="font-semibold text-[color:var(--dms-navy)]">{form.email}</span> a custom price and proof within one business day.</p>
            <Link to="/" className="btn-navy mt-8 inline-flex">Back to shop <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-12 bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-8 md:p-10 space-y-8">
            <div>
              <h3 className="font-serif text-xl text-[color:var(--dms-navy)]">Your contact</h3>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full name" required><input required value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} /></Field>
                <Field label="Email" required><input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} /></Field>
                <Field label="Phone"><input value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} /></Field>
                <Field label="Organization / church"><input value={form.organization} onChange={(e) => update("organization", e.target.value)} className={inputCls} /></Field>
              </div>
            </div>

            <div>
              <h3 className="font-serif text-xl text-[color:var(--dms-navy)]">The order</h3>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Occasion" required>
                  <select value={form.occasion} onChange={(e) => update("occasion", e.target.value)} className={inputCls}>
                    {OCCASIONS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Garment" required>
                  <select value={form.garment} onChange={(e) => update("garment", e.target.value)} className={inputCls}>
                    {GARMENTS.map((g) => <option key={g}>{g}</option>)}
                  </select>
                </Field>
                <Field label="How many shirts?" required><input required value={form.quantity} onChange={(e) => update("quantity", e.target.value)} placeholder="e.g. 30" className={inputCls} /></Field>
                <Field label="Date needed" required><input type="date" required value={form.dateNeeded} onChange={(e) => update("dateNeeded", e.target.value)} className={inputCls} /></Field>
                <Field label="Colors"><input value={form.colors} onChange={(e) => update("colors", e.target.value)} placeholder="e.g. Navy + Cream" className={inputCls} /></Field>
                <Field label="Size breakdown"><input value={form.sizes} onChange={(e) => update("sizes", e.target.value)} placeholder="e.g. 5S / 10M / 10L / 5XL" className={inputCls} /></Field>
              </div>
            </div>

            <div>
              <h3 className="font-serif text-xl text-[color:var(--dms-navy)]">The design</h3>
              <div className="mt-4 flex gap-3">
                {[["yes", "I have a design"], ["no", "Design it for me"]].map(([v, label]) => (
                  <button type="button" key={v} onClick={() => update("hasDesign", v)} className={`px-5 py-2.5 rounded-full border text-sm font-medium ${form.hasDesign === v ? "bg-[color:var(--dms-navy)] text-white border-[color:var(--dms-navy)]" : "bg-white text-[color:var(--dms-navy)] border-[color:var(--dms-navy)]/20 hover:border-[color:var(--dms-navy)]"}`}>{label}</button>
                ))}
              </div>
              <Field label="Design notes" className="mt-4">
                <textarea rows={4} value={form.designNotes} onChange={(e) => update("designNotes", e.target.value)} placeholder="Tell us the words, verse, or vibe you're going for…" className={inputCls} />
              </Field>
            </div>

            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-[color:var(--dms-navy)]/60 max-w-md">This is a quote request only — you will not be charged now.</p>
              <button type="submit" className="btn-peach">Send my request <ArrowRight className="w-4 h-4" /></button>
            </div>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-3 text-[color:var(--dms-navy)] focus:outline-none focus:border-[color:var(--dms-peach)] transition";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">{label}{required && <span className="text-[color:var(--dms-peach)]"> *</span>}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
